import React, { useState } from 'react';
import styled from 'styled-components';
import { PlacesType, Tooltip } from 'react-tooltip';
import { css } from '/imports/ui/css';
import { randomString } from '/imports/utils';

const ButtonStyled = styled.button`
  text-transform: capitalize;
  border: none;
  border-radius: 0.5rem;
  padding: 0 1rem;
  min-height: ${css.inputHeight};
  font-family: ${css.fontFamily};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-around;
  font-size: 1rem;
  align-items: center;
  cursor: pointer;
  position: relative;
  transition: background 0.2s ease, color 0.2s ease;
  &.primary {
    background: ${css.primaryColor};
    border: 1px solid ${css.primaryColor};
    color: #FFF;
    &:disabled {
      cursor: not-allowed;
      opacity: 0.75;
      background: ${css.grey5};
      border: 1px solid ${css.grey5};
      color: ${css.grey3};
      &:hover {
        background: ${css.grey5};
      }
    }
    &:hover {
      background: ${css.primaryColor2};
      border: 1px solid ${css.primaryColor2};
    }
  }
  &.secondary {
    background: ${css.grey6};
    border: 1px solid ${css.grey6};
    color: ${css.grey2};
    &:disabled {
      cursor: not-allowed;
      opacity: 0.75;
      background: ${css.grey5};
      color: ${css.grey3};
      &:hover {
        background: ${css.grey5};
        color: ${css.grey3};
      }
    }
    &:hover {
      background: ${css.secondaryColor2};
      border: 1px solid ${css.secondaryColor2};
    }
  }
  &.tertiary {
    background: #FFF;
    border: 1px solid ${css.secondaryColor};
    color: ${css.secondaryColor};
    &:disabled {
      cursor: not-allowed;
      opacity: 0.75;
      &:hover {
        background-color: ${css.secondaryColor};
        color: #FFF;
      }
    }
    &:hover {
      background: ${css.primaryColor};
      border: 1px solid ${css.primaryColor2};
      color: #FFF;
    }
  }
  &.compact {
    min-height: 1.5rem;
    font-size: 0.8rem;
    padding: 0 0.75rem;
    border-radius: 0.75rem;
  }
  &.caps {
    text-transform: uppercase;
  }
  &.medium {
    font-weight: 600;
  }
  .icon {
    svg {
      & {
        fill: ${css.white};
      }
    }
  }
`;

interface ButtonProps {
  id?: string,
  className?: string,
  style?: React.CSSProperties,
  type?: 'button' | 'submit',
  disabled?: boolean,
  onClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void,
  onDoubleClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void,
  children?: React.ReactNode,
  tip?: string,
  tipPlacement?: PlacesType,
  'data-tooltip-id'?: string;
  tipEvents?: {
    mouseenter?: boolean
    focus?: boolean
    mouseover?: boolean
    click?: boolean
    dblclick?: boolean
    mousedown?: boolean
  };
}

/** Styled button with an optional tooltip. */
export const Button = (props: ButtonProps) => {
  const className = `btn ${props.className || 'primary'}`;
  const [tipId,] = useState(props['data-tooltip-id'] || `tip${randomString()}`);
  return (
    <>
      <ButtonStyled
        id={props.id}
        className={className}
        type={props.type || 'button'}
        disabled={props.disabled}
        onClick={props.onClick}
        onDoubleClick={props.onDoubleClick}
        data-tooltip-id={tipId}
        {...props}
      >
        {props.children || null}
      </ButtonStyled>
      {
        props.tip ?
          <Tooltip id={tipId} content={props.tip} place={props.tipPlacement} openEvents={props.tipEvents || { mouseover: true }} />
          :
          null
      }
    </>
  );
};